import { postAdminRemoveFrontendAddonVersion } from "@/api/admin/frontendAddons";
import { postRemoveFrontendAddonVersion } from "@/api/frontendAddons";
import { useAppNotificationManager } from "@/components/AppProvider/AppNotificationProvider";
import { getDialogMessages } from "@/components/Dialog/DialogFirestore/helper";
import { formatFrontendAddonVersionDisplayName } from "@/components/FrontendAddonForm/helpers/frontendAddonVersionDisplay";
import { FrontendAddonVersionRaw } from "@/domain/FrontendAddon";
import React, { useMemo } from "react";
import {
  DialogPhaseType,
  useDialog,
} from "../../../../../Dialog/helpers/hooks";
import { FrontendAddonVersionAction } from "../types";

export const DialogFrontendAddonVersionSingleAction: React.FC<{
  organizationId: string;
  frontendAddonId: string;
  isAADeveloperOrAbove: boolean;
  versions: FrontendAddonVersionRaw[];
  selectedVersionId: string | null;
  setSelectedVersionId: React.Dispatch<React.SetStateAction<string | null>>;
  selectedAction: FrontendAddonVersionAction | null;
  setSelectedAction: React.Dispatch<
    React.SetStateAction<FrontendAddonVersionAction | null>
  >;
  dialogPhase: DialogPhaseType;
  setDialogPhase: React.Dispatch<React.SetStateAction<DialogPhaseType>>;
  fetchFrontendAddonVersions: () => Promise<void>;
  runReloadFrontendAddons?: () => Promise<void>;
}> = ({
  organizationId,
  frontendAddonId,
  isAADeveloperOrAbove,
  versions,
  selectedVersionId,
  setSelectedVersionId,
  selectedAction,
  setSelectedAction,
  dialogPhase,
  setDialogPhase,
  fetchFrontendAddonVersions,
  runReloadFrontendAddons,
}) => {
  const { showErrorNotification, showSuccessNotification } =
    useAppNotificationManager();
  const { renderDialogs } = useDialog({ dialogPhase, setDialogPhase });

  const selectedVersion = useMemo(
    () =>
      selectedVersionId
        ? versions.find((version) => version.id === selectedVersionId)
        : undefined,
    [selectedVersionId, versions]
  );
  const selectedVersionDisplayName = selectedVersion
    ? formatFrontendAddonVersionDisplayName(selectedVersion)
    : "バージョン";

  const clearAction = () => {
    setSelectedVersionId(null);
    setSelectedAction(null);
  };

  const runAction = async () => {
    if (!selectedVersionId || !selectedAction) return;
    setDialogPhase("in_progress");
    try {
      if (isAADeveloperOrAbove) {
        await postAdminRemoveFrontendAddonVersion({
          frontendAddonId,
          FrontendAddonVersionId: selectedVersionId,
        });
      } else {
        await postRemoveFrontendAddonVersion({
          organizationId,
          frontendAddonId,
          FrontendAddonVersionId: selectedVersionId,
        });
      }
      showSuccessNotification(messages.success.displayMessage);
      void runReloadFrontendAddons?.();
      void fetchFrontendAddonVersions();
      clearAction();
      setDialogPhase(null);
    } catch (error) {
      showErrorNotification(messages.error.displayMessage, error);
      setDialogPhase(null);
    }
  };

  // バージョンは無効化を持たず削除のみのため、文言は delete 系で統一する
  const messages = getDialogMessages({
    isSome: false,
    actionType: "delete",
    targetType: "version",
    targetName: selectedVersionDisplayName,
  });

  return (
    <>
      {renderDialogs({
        confirm: {
          title: messages.confirm.title,
          displayMessage: messages.confirm.displayMessage,
          buttonText: messages.confirm.buttonText,
          onClick: runAction,
          onClickCancel: clearAction,
          isThemeRed: true,
        },
        inProgress: {
          title: messages.inProgress.title,
        },
      })}
    </>
  );
};
